<template>
    <ds-modal
        class="email-charges-modal"
        data-qa="email-charges-modal"
        :is-open="isOpen"
        size="med"
        show-close
        :title="$t('myaccount.usage.email.viewcharges.title')"
        @close="$emit('close')"
    >
        <div class="modal-container">
            <p class="sub-heading">
                {{ $t('myaccount.usage.email.viewcharges.subheading') }}
            </p>
            <p class="total-charge" data-qa="total-charge">
                {{ formattedTotalCost }}
            </p>

            <div class="charging-detail">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <p v-html="getChargingDetailText" />
                <ul class="overage-fee-requirements">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <li v-html="getEmailSentDetailText" />

                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <li v-html="getUniqueRecipientRatioDetailText" />
                </ul>
            </div>

            <div class="overage-detail-container">
                <ds-card
                    class="overage-detail-button"
                    elevation="z0"
                    @click="isCollapsibleOpen = !isCollapsibleOpen"
                >
                    <ds-card-header>
                        <div class="overage-detail-heading">
                            <span>{{ $t('myaccount.usage.email.viewcharges.fee.calculation.title') }}</span>
                            <ds-icon v-if="isCollapsibleOpen" name="chevron-up" />
                            <ds-icon v-else name="chevron-down" />
                        </div>
                    </ds-card-header>
                    <ds-card-body v-show="isCollapsibleOpen">
                        <div class="overage-detail-summary" data-qa="overage-detail-summary">
                            <ol>
                                <li>{{ getStep1CalculationText }}</li>
                                <li>{{ getStep2CalculationText }}</li>
                                <li>{{ getStep3CalculationText }}</li>
                            </ol>
                        </div>
                    </ds-card-body>
                </ds-card>
            </div>

            <div class="more-information">
                <p>
                    For more information about overage fees, please see <a href="https://help.keap.com/help/email-usage-and-overages" target="_blank">this help article</a> or call {{ keapSalesNumber }}.
                </p>
            </div>
        </div>
    </ds-modal>
</template>

<script>
export default {
    props: {
        isOpen: {
            type: Boolean,
        },
        totalCost: {
            type: String,
        },
        currencySymbol: {
            type: String,
        },
        currencyCode: {
            type: String,
        },
        emailSentLimit: {
            type: Number,
        },
        emailSentActual: {
            type: Number,
        },
        uniqueRecipientRatioLimit: {
            type: Number,
        },
        uniqueRecipientRatioActual: {
            type: Number,
        },
        emailUnitPrice: {
            type: String,
        },
        uniqueAddressesSentTo: {
            type: Number,
        },
    },

    emits: ['close'],

    data() {
        return {
            keapSalesNumber: '+1 (866) 800-0004',
            isCollapsibleOpen: false,
        };
    },

    computed: {
        formattedTotalCost() {
            // Should look like $100.00
            return `${this.currencySymbol}${this.totalCost}`;
        },

        getChargingDetailText() {
            return 'You must satisfy <strong>both</strong> requirements to be charged overage fees:';
        },

        getEmailSentDetailText() {
            const emailSentWithCommas = this.numberWithCommas(this.emailSentLimit);
            const emailSentActualWithCommas = this.numberWithCommas(this.emailSentActual);

            return `Exceed ${emailSentWithCommas} emails sent (you sent <strong>${emailSentActualWithCommas}</strong>)`;
        },

        getUniqueRecipientRatioDetailText() {
            return `Average over ${this.uniqueRecipientRatioLimit} unique recipients per email (you averaged <strong>${this.uniqueRecipientRatioActual}</strong>)`;
        },

        getStep1CalculationText() {
            const result = this.uniqueRecipientRatioLimit * this.uniqueAddressesSentTo;

            const resultFormatted = this.numberWithCommas(result);
            const rateLimitFormatted = this.numberWithCommas(this.uniqueRecipientRatioLimit);
            const uniqueFormatted = this.numberWithCommas(this.uniqueAddressesSentTo);

            return this.$t('myaccount.usage.email.viewcharges.step1', {
                rateLimitFormatted,
                resultFormatted,
                uniqueFormatted,
            });
        },

        getStep2CalculationText() {
            const step1Math = this.uniqueRecipientRatioLimit * this.uniqueAddressesSentTo;
            const overage = this.emailSentActual - step1Math;

            const step1MathFormatted = this.numberWithCommas(step1Math);
            const overageFormatted = this.numberWithCommas(overage);
            const emailSentActualFormatted = this.numberWithCommas(this.emailSentActual);

            return this.$t('myaccount.usage.email.viewcharges.step2', {
                emailSentActualFormatted,
                step1MathFormatted,
                overageFormatted,
            });
        },

        getStep3CalculationText() {
            const step1Math = this.uniqueRecipientRatioLimit * this.uniqueAddressesSentTo;
            const step2Math = this.emailSentActual - step1Math;

            const step2MathFormatted = this.numberWithCommas(step2Math);
            const pricePerUnit = this.currencySymbol + this.emailUnitPrice;

            return this.$t('myaccount.usage.email.viewcharges.step3', {
                pricePerUnit,
                step2MathFormatted,
                formattedTotalCost: this.formattedTotalCost,
            });
        },
    },

    methods: {
        numberWithCommas(x) {
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
    },
};
</script>

<style lang="scss" scoped>
.modal-container {
    display: flex;
    flex-direction: column;
    margin: -1rem -1rem 0
}

.total-charge {
    font-size: 2rem;
}

.charging-detail {
    margin-bottom: $spacing-200;
}

.overage-fee-requirements {
    list-style-type: disc;
    list-style-position: inside;
    padding-left: $spacing-100;
}

.overage-detail-container {
    display: flex;
    padding: $spacing-100 $spacing-200;
    border-radius: 0.75rem;
    border: 1px solid $color-ink-200;
    box-shadow: none;
    margin-bottom: $spacing-200;
}

.overage-detail-button {
    height: 100%;
    box-shadow: none;
}

.overage-detail-button:hover, .overage-detail-button:focus {
    box-shadow: none;
}

.ds-card-body {
    padding-bottom: 0;
}

.overage-detail-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-200;
    flex: 1;
}

.overage-detail-summary {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-100;
    align-self: stretch;

    ol {
        margin: 0;
        padding: 0;
    }

    li {
        padding-left: $spacing-100;
        padding-bottom: $spacing-100;
    }
}

.more-information {
    width: 80%;

    p {
        margin: 0;
    }
}
</style>
